"use client";

import { useRef } from "react";
import { useMatch } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAppEvent } from "@/lib/events";

/**
 * Subscribes to the `praetor-revision-saved` app event (fired by chatModelAdapters when the
 * SSE stream emits `data-praetor-revision-saved`) and surfaces a transient toast for the
 * thread the user is currently viewing. No in-app artefact to render — the new revision lives
 * in PraetorIS, not as a thread-scoped generated document. Cold-reload UX is covered by the
 * persisted ToolCallMessagePart for `save_revision` (its localized title comes from
 * `NativeTool_save_revision`), not by this bridge.
 *
 * <p>Mount once at the layout level so it lives for the lifetime of the SPA. The bridge sits
 * inside <BrowserRouter> but outside <Routes>, so we use <code>useMatch</code> on the thread
 * route pattern to read the current threadId from the URL — <code>useParams</code> would not
 * resolve here because the component isn't rendered inside a Route.</p>
 *
 * <p>C4 fix: previously the toast fired on any open SPA tab regardless of which thread emitted
 * the event. We now scope toast emission to the active thread by comparing the event's
 * <code>threadId</code> against the URL. A payload with no <code>threadId</code> falls through
 * to the active thread (backwards-compat with any future emitter that omits it).</p>
 */
export function PraetorRevisionToastBridge() {
  const { t } = useTranslation();
  const match = useMatch("/:caseId/:threadId");
  const activeThreadId = match?.params.threadId ?? null;

  // useAppEvent re-subscribes only on `name` change; closure-captured values
  // are stale-by-design. Mirror activeThreadId into a ref so the handler reads
  // the current value at fire time.
  const activeThreadIdRef = useRef(activeThreadId);
  activeThreadIdRef.current = activeThreadId;

  useAppEvent("praetor-revision-saved", (payload) => {
    const eventThreadId = payload?.threadId ?? null;
    // Show the toast when:
    //  - the event carries no threadId (legacy emitter), OR
    //  - the event's threadId matches the active thread the user is viewing.
    if (eventThreadId !== null && eventThreadId !== activeThreadIdRef.current) {
      return;
    }
    toast.success(t("praetorRevisionSaved.toast"));
  });

  return null;
}
